import React, { useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { Link } from 'react-router-dom';
import normalizeString from 'components/Dashboard/utilities/normalizeString';
import idsWithoutImages from './idsWithoutImages';
import ReactGA from 'react-ga4';
import TagsLists from 'components/Dashboard/TagsList';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import getAppSite from 'utils/getAppSite';
import { useAuth0 } from '@auth0/auth0-react';
import time from 'utils/time';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import useItemController from 'components/Dashboard/ItemController/useItemController';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import { OrganisationLogo } from 'components/Dashboard/UserContent/ui/InfluenceListItem';
import isValidURL from 'components/Dashboard/utilities/isValidURL';
import RatingUpdateBadge from 'components/Dashboard/PriorityUpdates/RatingUpdateBadge';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  monthsShort: ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
});

const ItemNew = (props) => {
  const {
    explanations,
    item,
    itemOnclick,
    index,
    notShowDescriptionAndShare,
    scrollBarRef,
    feedItemFlags: propsFeedItemFlags,
    array,
    embedShowTags,
    notShowShare,
    itemFromWidget,
    nameSection,
    listView,
    openInNewTab,
    switchRelevantExtractOrSummary,
    hideYellowDot,
    showContentTypeBadge,
    thumbDownProps,
    hideImage,
    tagsFixedPosition,
    itemActionsProps,
    zIndex,
    onlyEmbargoedTag,
    hideOneSentenceSummary,
    tooltipText,
    showPriorityBadge,
    setActiveUpdate,
  } = props;

  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const { search } = state;

  const { list, activeSearch } = search;
  const { KeyUpdates } = contentResults;
  const feedItemFlags = propsFeedItemFlags ?? KeyUpdates?.feedItemFlags ?? [];
  const { rememberPosition } = useRememberPosition();
  const { isAuthenticated } = useAuth0();

  const imageCondition = !itemFromWidget && isAuthenticated && !hideImage;

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useItemController({
    item,
    index,
    propsFeedItemFlags,
    itemFromWidget,
    hideYellowDot,
    thumbDownProps,
    showPriorityBadge,
    itemName: 'KeyUpdate',
  });

  const createImageUrl = () => {
    let source = item?.source;
    if (source && isValidURL(source)) {
      const url = new URL(source);
      source = `https://img.logo.dev/${url.hostname}`;
    }
    return source;
  };

  return (
    <>
      <Link
        to={locationDescriptor(`/key-updates/${item.id}/${normalizeString(item.title)}`)}
        className={`content-item-link`}
        style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={(e) => {
          if (rateUpdatesMode && setActiveUpdate) {
            preventLink(e);
            setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
            return;
          }
          if (scrollBarRef) {
            rememberPosition('KeyUpdates', scrollBarRef, index);
          }
          if (itemOnclick) {
            itemOnclick(e);
          }
          if (item.splitFilterNoActivity) {
            ReactGA.event({
              category: 'Blank Stakeholder Key Update Click',
              action: `${item.splitFilterNoActivity} click`,
              label: item.contentUrl,
            });
          }
        }}
        data-tooltip-content={tooltipText}
        data-tooltip-id={'general-tooltip'}
        data-tooltip-class-name='header-tooltip'
        data-tooltip-place='top'
      >
        <li className={`my-2 content-item`}>
          {unseenConditional && (
            <>
              <span className='unseen-dot' />
            </>
          )}
          <div className='py-3 px-4'>
            <div className='d-flex align-items-baseline mb-2 ml-2'>
              {showContentTypeBadge ? (
                <div className='mt-1 specific-bottom-margin-4 flex-grow-1 text-light-blue blue-icon content-type-badge flex-centered justify-content-between'>
                  <ContentTypeBadge objectType={item.type} />
                </div>
              ) : (
                <h4 className={`mb-0 flex-grow-1 content-item-title`}>{item.title}</h4>
              )}
              {!notShowShare && (
                <div className='flex-centered'>
                  {rateUpdatesMode ? (
                    <>
                      {feedItemFlags?.[index]?.importanceRating && (
                        <RatingUpdateBadge
                          item={{ rating: TransformFromPascalCase(feedItemFlags?.[index]?.importanceRating) }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {priorityBadgeCondition && <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} />}
                      <GeneralItemActions
                        item={item}
                        index={index}
                        nameSection={nameSection}
                        thumbDownProps={thumbDownProps}
                        itemActionsProps={itemActionsProps}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
            {showContentTypeBadge && (
              <div className='flex-centered mb-2 ml-2'>
                <h4 className={`mb-0 flex-grow-1 content-item-title`}>{item.title}</h4>
              </div>
            )}
            <div className='row mx-0 align-items-start'>
              <div className={`content-title flex-grow-1 ${onlyEmbargoedTag ? '' : 'pr-lg-5'} pr-2 ml-2`}>
                {!notShowDescriptionAndShare && (
                  <>
                    <RelevantExtract
                      item={item}
                      listView={listView}
                      switchRelevantExtractOrSummary={switchRelevantExtractOrSummary}
                      hideOneSentenceSummary={hideOneSentenceSummary}
                    />
                  </>
                )}

                <p className='heading-date flex-grow-1 px-0 mb-0'>
                  {getAppSite() === 'usa' && (
                    <>
                      {(item.source || item.sourceOrganisation) && (
                        <>
                          <span className='paragraph-p4'>
                            {item.source ? new URL(item.source)?.hostname : item.sourceOrganisation}
                          </span>
                          <span className='dot'></span>
                        </>
                      )}
                      {item.sourceTags && (
                        <>
                          {item?.sourceTags?.map((sourceTag, index) => (
                            <React.Fragment key={index}>
                              <span className='paragraph-p4'>{sourceTag}</span>
                              <span className='dot'></span>
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  <span
                    className='date'
                    data-tooltip-id='general-tooltip'
                    data-tooltip-content={
                      feedItemFlags[index]?.addedToFeedUtc
                        ? `Added to your feed on ${time(feedItemFlags[index]?.addedToFeedUtc).formatGeneralDate(true)}`
                        : undefined
                    }
                  >
                    {time(item.dateTime).formatGeneralDate()}
                  </span>
                  {getAppSite() !== 'usa' && (
                    <>
                      <span className='dot'></span>
                      {time(item.dateTime).formatTime()}
                    </>
                  )}
                </p>

                {((list !== null && activeSearch && !itemFromWidget) || embedShowTags) && (
                  <TagsLists
                    {...props}
                    explanations={explanations ?? KeyUpdates?.explanations}
                    feedItemFlags={feedItemFlags}
                    index={index}
                    section={'KeyUpdates'}
                    embargoed={item.embargoed}
                    embargoedUntil={item.embargoedUntil}
                    tagsFixedPosition={tagsFixedPosition}
                    scrollBarRef={scrollBarRef}
                    onlyEmbargoedTag={onlyEmbargoedTag}
                  />
                )}
              </div>
              {imageCondition && (
                <>
                  {getAppSite() === 'usa' ? (
                    <div className=' d-none d-sm-block'>
                      <OrganisationLogo notShowDefaultImage={true} imageUrl={createImageUrl()} index={index} />
                    </div>
                  ) : (
                    <div
                      className='content-image d-none d-sm-block'
                      style={{
                        backgroundImage: item.imageUrl
                          ? `url(${item.imageUrl})`
                          : item.imageId !== 0 && !idsWithoutImages.includes(item.imageId)
                            ? `url('https://www.parliamenttoday.com/image.php?imageid=${item.imageId}&mode=1')`
                            : `url('${import.meta.env.VITE_APP_CDNURL}/images/keyupdates-placeholder.png')`,
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </li>
      </Link>
    </>
  );
};

const RelevantExtract = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { sectionsView } = state;

  const { item, listView, switchRelevantExtractOrSummary, hideOneSentenceSummary } = props;
  const { oneSentenceSummary } = item ?? {};
  const [heightItem, setHeightItem] = useState(0);
  const [translateValue, setTranslateValue] = useState(0);
  const [animated, setAnimated] = useState();

  const { selectedOptionRelevantExtract } = sectionsView ?? {};
  const [localSelectedOption, setLocalSelectedOption] = useState(selectedOptionRelevantExtract);
  const sentenceSummaryComponent = useRef(null);
  const titleBodyComponent = useRef(null);

  const isMountedComponent = useRef(false);
  const oneAiSummary = selectedOptionRelevantExtract === 'One-line AI summary';

  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item });

  useEffect(() => {
    const createHeight = () => {
      let bodyElement = titleBodyComponent?.current;
      let sentenceElement = sentenceSummaryComponent?.current;

      let elHeight = selectedOptionRelevantExtract === 'Most relevant extract' ? bodyElement : sentenceElement;
      setTranslateValue(oneAiSummary ? bodyElement?.getBoundingClientRect()?.height + 7.5 : 0);
      setHeightItem(elHeight?.getBoundingClientRect()?.height + 7.5);
    };
    createHeight();
    setTimeout(() => {
      isMountedComponent.current = true;
    }, 100); //value while the text gets in the correct position
  }, [selectedOptionRelevantExtract]);

  useEffect(() => {
    if (selectedOptionRelevantExtract !== localSelectedOption) {
      setLocalSelectedOption(selectedOptionRelevantExtract);
      setTimeout(() => {
        setAnimated(true);
        setTimeout(() => {
          setAnimated(false);
        }, 1000); //value of shimmer animation
      }, 500); //Value of dropdown animation
    }
  }, [selectedOptionRelevantExtract]);

  return (
    <div
      className='overflow-hidden relevant-extract-keyupdates'
      style={{
        height: `${!!oneSentenceSummary && switchRelevantExtractOrSummary ? `${heightItem}px` : 'auto'}`,
      }}
    >
      <p
        className={`${listView ? 'title-body-before position-relative' : ''} ${
          isMountedComponent.current && listView ? 'title-body-transition' : ''
        } mb-2 title-body ${animated ? 'title-body-animated' : ''}`}
        style={
          !!oneSentenceSummary && listView
            ? {
                transform: `translateY(-${translateValue}px)`,
                opacity: oneAiSummary ? 0 : undefined,
                transition: oneAiSummary ? 'all 500ms' : undefined,
              }
            : null
        }
        ref={titleBodyComponent}
        dangerouslySetInnerHTML={createMarkup(bodySnippet)}
      />
      {!!oneSentenceSummary && switchRelevantExtractOrSummary && !hideOneSentenceSummary && (
        <p
          className={`${listView ? 'title-body-before' : ''} ${
            isMountedComponent.current ? 'title-body-transition' : ''
          } title-body ${animated ? 'title-body-animated' : ''} ${!(!!oneSentenceSummary && listView) ? 'mb-2' : ''}`}
          style={
            !!oneSentenceSummary && listView
              ? {
                  transform: `translateY(-${translateValue}px)`,
                }
              : null
          }
          ref={sentenceSummaryComponent}
          dangerouslySetInnerHTML={createMarkup(oneSentenceSummary)}
        />
      )}
    </div>
  );
};

export default ItemNew;
